import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Header } from "@/components/admin/BookingsTab";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import DriverWalletDialog from "@/components/admin/DriverWalletDialog";

const STATUS_COLORS = {
  available: "bg-emerald-400/20 text-emerald-300 border-emerald-400/40",
  on_trip: "bg-violet-400/20 text-violet-300 border-violet-400/40",
  offline: "bg-slate-400/20 text-slate-300 border-slate-400/40",
};

export default function DriversTab({ isSuper }) {
  const [drivers, setDrivers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", vehicle: "", plate: "", status: "available" });

  const load = async () => {
    try { const { data } = await api.get("/drivers"); setDrivers(data); }
    catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/drivers", form); toast.success("Driver added"); setOpen(false); setForm({ name: "", phone: "", vehicle: "", plate: "", status: "available" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const updateStatus = async (id, status) => {
    try { await api.patch(`/drivers/${id}`, { status }); toast.success("Updated"); load(); }
    catch { toast.error("Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this driver?")) return;
    try { await api.delete(`/drivers/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <Header title="Drivers" subtitle="Fleet & availability">
        {isSuper && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="btn-gold rounded-full" data-testid="drivers-add"><Plus className="w-4 h-4 mr-2" /> Add driver</Button></DialogTrigger>
            <DialogContent className="bg-[#111827] border-white/10 text-white">
              <DialogHeader><DialogTitle className="font-display">New driver</DialogTitle></DialogHeader>
              <form onSubmit={add} className="grid gap-4">
                {[["name", "Name"], ["phone", "Phone"], ["vehicle", "Vehicle"], ["plate", "Plate"]].map(([k, l]) => (
                  <div key={k}><Label className="text-xs uppercase text-slate-400">{l}</Label><Input required value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="bg-black/30 border-white/10 mt-1 h-10" /></div>
                ))}
                <Button type="submit" className="btn-gold">Save</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </Header>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {drivers.map((d) => (
          <div key={d.id} className="glass-card rounded-2xl p-5 gold-border" data-testid={`driver-card-${d.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-display font-bold text-lg">{d.name}</div>
                <div className="text-xs text-slate-500 font-mono">{d.phone}</div>
              </div>
              <Badge className={`${STATUS_COLORS[d.status]} border font-mono uppercase text-[10px] tracking-wider`}>{d.status.replace("_", " ")}</Badge>
            </div>
            <div className="mt-4 pt-4 border-t border-white/5 space-y-1 text-sm">
              <div className="text-slate-300">{d.vehicle}</div>
              <div className="text-slate-500 font-mono text-xs">{d.plate}</div>
            </div>
            <div className="mt-4 flex gap-2">
              <Select value={d.status} onValueChange={(v) => updateStatus(d.id, v)}>
                <SelectTrigger className="bg-black/30 border-white/10 h-9 flex-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#111827] border-white/10 text-white">
                  {["available", "on_trip", "offline"].map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
                </SelectContent>
              </Select>
              <DriverWalletDialog driver={d} />
              {isSuper && (
                <Button variant="outline" size="icon" onClick={() => del(d.id)} className="bg-transparent border-red-500/30 text-red-400 hover:bg-red-500/10 h-9 w-9"><Trash2 className="w-4 h-4" /></Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
